import { KeyCode } from "@/common/constants/keyValue";
import { useEffect, useRef } from "react";

export interface HotkeySpec {
	code: KeyCode;
	alt?: boolean;
	shift?: boolean;
}

/**
 * True where the agent is typing: plain inputs, and the rich-text editors, which are contenteditable.
 */
const isTypingInto = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}

	if (target.isContentEditable) {
		return true;
	}

	return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
};

/**
 * Runs a handler when a key combination is pressed anywhere in the app. Teams owns the keyboard
 * until the user clicks inside CC4Teams, so the shortcut only fires once the app has focus.
 */
export const useGlobalHotkey = (
	spec: HotkeySpec,
	handler: () => void,
	enabled = true
): void => {
	const handlerRef = useRef(handler);

	useEffect(() => {
		handlerRef.current = handler;
	}, [handler]);

	const { code, alt, shift } = spec;

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.repeat || event.ctrlKey || event.metaKey) {
				return;
			}

			// A shortcut is not allowed to swallow a keystroke meant for a message being composed. Alt is
			// no part of one, so a spec that requires it stays live wherever focus is, including the
			// contacts search box that a tooltip advertises Alt+Shift+F beside.
			if (!alt && isTypingInto(event.target)) {
				return;
			}

			if (
				event.code !== String(code) ||
				event.altKey !== !!alt ||
				event.shiftKey !== !!shift
			) {
				return;
			}

			event.preventDefault();
			handlerRef.current();
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [enabled, code, alt, shift]);
};
